/**
 * DatePicker Component - Controller
 * =================================
 */

import type { IScope, IAugmentedJQuery, IDocumentService, ISCEService, INgModelController, ITranscludeFunction } from 'angular';
import { WEEKDAYS, MONTHS, MONTHS_SHORT, SIZES, BASE_CLASSES, ICONS, DEFAULTS, DatePickerSize } from './constants';

export interface DatePickerBindings {
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: DatePickerSize;
  onChange?: (args: { value: string; date: Date | null }) => void;
}

interface CalendarCell {
  day: number | null;
}

export class DatePickerController implements DatePickerBindings {
  static $inject = ['$scope', '$element', '$document', '$sce', '$transclude'];

  private $scope: IScope | null;
  private $element: IAugmentedJQuery | null;
  private $document: IDocumentService | null;
  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  ngModelCtrl?: INgModelController | null;

  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  helpMessage?: string;
  error?: boolean;
  disabled?: boolean;
  size?: DatePickerSize;
  onChange?: (args: { value: string; date: Date | null }) => void;

  weekdays: string[] = WEEKDAYS;
  open = false;
  viewMonth = 0;
  viewYear = 0;
  selectedDate: Date | null = null;
  days: CalendarCell[] = [];
  monthLabel = '';
  monthName = '';
  yearLabel = '';

  trustedChevron: unknown = null;
  trustedPrev: unknown = null;
  trustedNext: unknown = null;
  labelId = '';

  // Slots nombrados (label/help): detectados con isSlotFilled, que SÍ funciona
  // para multi-slot transclusion (a diferencia del slot default booleano).
  hasLabelSlot = false;
  hasHelpSlot = false;

  private _onDocClick: (e: Event) => void;

  constructor(
    $scope: IScope,
    $element: IAugmentedJQuery,
    $document: IDocumentService,
    $sce: ISCEService,
    $transclude: ITranscludeFunction
  ) {
    this.$scope = $scope;
    this.$element = $element;
    this.$document = $document;
    this.$sce = $sce;
    this.$transclude = $transclude;

    this._onDocClick = (e: Event): void => {
      if (this.open && this.$element && !this.$element[0].contains(e.target as Node)) {
        if (this.$scope) {
          this.$scope.$applyAsync(() => { this.open = false; });
        }
      }
    };
  }

  $onInit(): void {
    this.size = this.size || DEFAULTS.size;
    this.ariaLabel = this.ariaLabel || 'Seleccionar fecha';
    const uid = this.$scope ? String(this.$scope.$id) : Math.random().toString(36).slice(2);
    this.labelId = 'ec-date-label-' + uid;

    if (this.$transclude) {
      this.hasLabelSlot = this.$transclude.isSlotFilled('labelSlot');
      this.hasHelpSlot = this.$transclude.isSlotFilled('helpSlot');
    }

    const today = new Date();
    this.viewMonth = today.getMonth();
    this.viewYear = today.getFullYear();

    if (this.$sce) {
      this.trustedChevron = this.$sce.trustAsHtml(ICONS.chevron);
      this.trustedPrev = this.$sce.trustAsHtml(ICONS.prev);
      this.trustedNext = this.$sce.trustAsHtml(ICONS.next);
    }

    if (this.ngModelCtrl) {
      this.ngModelCtrl.$render = () => {
        const v = this.ngModelCtrl ? this.ngModelCtrl.$viewValue : null;
        this.selectedDate = this._parseDate(v);
        if (this.selectedDate) {
          this.viewMonth = this.selectedDate.getMonth();
          this.viewYear = this.selectedDate.getFullYear();
        }
        this._buildCalendar();
      };
    }

    this._buildCalendar();

    if (this.$document) {
      // Capture-phase (patrón de Select): cierra el popover al click afuera.
      (this.$document[0] as Document).addEventListener('click', this._onDocClick, true);
    }
  }

  $onDestroy(): void {
    if (this.$document) {
      (this.$document[0] as Document).removeEventListener('click', this._onDocClick, true);
    }
    this.$scope = null;
    this.$element = null;
    this.$document = null;
    this.$sce = null;
    this.$transclude = null;
    this.ngModelCtrl = null;
  }

  private _parseDate(v: unknown): Date | null {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v));
    if (!m) return null;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  private _toIso(d: Date): string {
    const pad = (n: number): string => (n < 10 ? '0' + n : String(n));
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  private _buildCalendar(): void {
    this.monthName = MONTHS[this.viewMonth];
    this.yearLabel = String(this.viewYear);
    this.monthLabel = this.monthName + ' ' + this.viewYear;
    const firstWeekday = (new Date(this.viewYear, this.viewMonth, 1).getDay() + 6) % 7; // lunes = 0
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const cells: CalendarCell[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push({ day: null });
    for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d });
    while (cells.length % 7 !== 0) cells.push({ day: null });
    this.days = cells;
  }

  toggle($event: Event): void {
    if (this.disabled) return;
    $event.stopPropagation();
    this.open = !this.open;
    if (this.open && this.selectedDate) {
      this.viewMonth = this.selectedDate.getMonth();
      this.viewYear = this.selectedDate.getFullYear();
      this._buildCalendar();
    }
  }

  prevMonth($event: Event): void {
    $event.stopPropagation();
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear -= 1; }
    else { this.viewMonth -= 1; }
    this._buildCalendar();
  }

  nextMonth($event: Event): void {
    $event.stopPropagation();
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear += 1; }
    else { this.viewMonth += 1; }
    this._buildCalendar();
  }

  selectDay(cell: CalendarCell, $event: Event): void {
    if (cell.day == null) return;
    $event.stopPropagation();
    this.selectedDate = new Date(this.viewYear, this.viewMonth, cell.day);
    this.open = false;
    const iso = this._toIso(this.selectedDate);
    if (this.ngModelCtrl) {
      this.ngModelCtrl.$setViewValue(iso);
    }
    if (typeof this.onChange === 'function') {
      this.onChange({ value: iso, date: this.selectedDate });
    }
  }

  handleKeydown($event: KeyboardEvent): void {
    if ($event.key === 'Escape') {
      this.open = false;
    }
  }

  isSelected(cell: CalendarCell): boolean {
    return cell.day != null && this.selectedDate != null &&
      this.selectedDate.getFullYear() === this.viewYear &&
      this.selectedDate.getMonth() === this.viewMonth &&
      this.selectedDate.getDate() === cell.day;
  }

  dayAriaLabel(cell: CalendarCell): string {
    return cell.day + ' de ' + MONTHS[this.viewMonth] + ' de ' + this.viewYear;
  }

  getWrapperClasses(): string[] { return [BASE_CLASSES.wrapper]; }
  getRelativeClasses(): string[] { return [BASE_CLASSES.relative]; }

  getTriggerClasses(): string[] {
    const classes = [BASE_CLASSES.trigger, SIZES[this.size || DEFAULTS.size].trigger, SIZES[this.size || DEFAULTS.size].text];
    if (this.disabled) classes.push(BASE_CLASSES.triggerDisabled);
    return classes;
  }

  // Error (patrón field §1): Figma no pinta borde — el color va en el texto del valor.
  // Colores mutuamente excluyentes (nunca combinar triggerTextColor + triggerTextError).
  getTriggerTextClasses(): string[] {
    return this.error
      ? [BASE_CLASSES.triggerText, BASE_CLASSES.triggerTextError]
      : [BASE_CLASSES.triggerText, BASE_CLASSES.triggerTextColor];
  }

  getPlaceholderClasses(): string[] { return [BASE_CLASSES.triggerText, BASE_CLASSES.triggerPlaceholder]; }
  getChevronClasses(): string[] { return [BASE_CLASSES.chevron]; }
  getPanelClasses(): string[] { return [BASE_CLASSES.panel]; }
  getHeaderClasses(): string[] { return [BASE_CLASSES.header]; }
  getNavClasses(): string[] { return [BASE_CLASSES.navBtn]; }
  getNavIconClasses(): string[] { return [BASE_CLASSES.navIcon]; }
  getMonthYearClasses(): string[] { return [BASE_CLASSES.monthYear]; }
  getMonthLabelClasses(): string[] { return [BASE_CLASSES.monthLabel]; }
  getWeekRowClasses(): string[] { return [BASE_CLASSES.weekRow]; }
  getWeekdayClasses(): string[] { return [BASE_CLASSES.weekday]; }
  getGridClasses(): string[] { return [BASE_CLASSES.grid]; }

  getDayClasses(cell: CalendarCell): string[] {
    if (cell.day == null) return [BASE_CLASSES.dayEmpty];
    const classes = [BASE_CLASSES.day];
    classes.push(this.isSelected(cell) ? BASE_CLASSES.daySelected : BASE_CLASSES.dayDefault);
    return classes;
  }

  getLabelClasses(): string[] { return [BASE_CLASSES.label]; }
  getHelpClasses(): string[] {
    return this.error
      ? [BASE_CLASSES.help, BASE_CLASSES.helpError]
      : [BASE_CLASSES.help, BASE_CLASSES.helpColor];
  }

  get displayText(): string | null {
    if (!this.selectedDate) return null;
    return this.selectedDate.getDate() + ' ' + MONTHS_SHORT[this.selectedDate.getMonth()] + ' ' + this.selectedDate.getFullYear();
  }
}

export default DatePickerController;
